import type { FC } from 'react';

import type { THomeDelivery } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import {
  Divider,
  Icon,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import { getDay, getMonth } from '@/modules/perseo-billing/utils';

import SummaryQuantity from './summary-quantity.component';

type TDeliverySummary = {
  homeDelivery?: THomeDelivery;
  quantity: number;
  totalDeliveries: number;
  isExpanded: boolean;
};

type THomeDeliveryDetail = {
  homeDelivery: THomeDelivery;
  index: number;
  totalDeliveries: number;
};

const formatAddress = (
  context: 'summary' | 'detail',
  homeDelivery?: THomeDelivery
) => {
  const streetName = homeDelivery?.streetName;
  const streetNumber = homeDelivery?.streetNumber;
  const streetComplement = homeDelivery?.streetComplement || '';
  const commune = homeDelivery?.commune;
  const region = homeDelivery?.region || '';
  const addressAlias = homeDelivery?.addressAlias;

  let address = `${streetName} ${streetNumber}`;
  const hasComuneAndRegion = commune && region;
  let addressDetails = hasComuneAndRegion
    ? `${commune} | ${region}`
    : region || commune || '';

  if (context === 'detail' && !addressAlias) {
    address = `${streetName} ${streetNumber} ${streetComplement} | ${commune}`;
    addressDetails = region;
  }

  return { address, addressDetails };
};

const formatDate = (homeDelivery: THomeDelivery) => {
  const shippingServiceName = homeDelivery?.shippingServiceName;
  const deliveryDateString = homeDelivery?.deliveryDateFrom;

  let dateLabel = '';
  if (!shippingServiceName) {
    const month = getMonth({
      dateString: deliveryDateString,
      responseFormat: 'short'
    });
    const day = getDay({
      dateString: deliveryDateString,
      responseFormat: 'numeric'
    });
    dateLabel = `el ${day} de ${month}`;
  }
  if (shippingServiceName === 'SAME_DAY') {
    dateLabel = 'hoy';
  }
  if (shippingServiceName === 'NEXT_DAY') {
    dateLabel = 'mañana';
  }
  if (shippingServiceName === 'DATE_RANGE') {
    const deliveryStartDateString = homeDelivery?.deliveryDateFrom.replaceAll(
      '-',
      '/'
    );
    const deliveryDateEndString = homeDelivery?.deliveryDateTo.replaceAll(
      '-',
      '/'
    );
    dateLabel = `del ${deliveryStartDateString} al ${deliveryDateEndString}`;
  }
  if (shippingServiceName === 'SCHEDULED') {
    const month = getMonth({
      dateString: deliveryDateString,
      responseFormat: 'short'
    });
    const day = getDay({
      dateString: deliveryDateString,
      responseFormat: 'numeric'
    });
    dateLabel = `el ${day} de ${month}`;
  }

  return dateLabel;
};

const ICON_PROPS = {
  className:
    'co-fill-pds-information-500 co-text-pds-information-500 co-shrink-0',
  size: { width: 24, height: 24 }
};

const HomeDeliverySummary: FC<TDeliverySummary> = ({
  homeDelivery,
  totalDeliveries,
  quantity,
  isExpanded
}) => {
  const { address, addressDetails } = formatAddress('summary', homeDelivery);
  const addressAlias = homeDelivery?.addressAlias;

  return (
    <div
      className="co-w-full co-h-full co-flex co-items-stretch co-gap-8px lg:co-p-8px co-bg-white lg:co-bg-pds-neutral-100 co-rounded-4px"
      tabIndex={0}
    >
      {totalDeliveries >= 2 && (
        <>
          <SummaryQuantity quantity={quantity} />
          <span className="co-w-1 co-flex-grow co-border-l-1 co-border-[#E6E6E6] "></span>
        </>
      )}
      <div className="co-w-full co-flex co-flex-col co-gap-8px co-overflow-hidden">
        <div className="co-flex co-items-center co-gap-4px">
          <Icon icon="TruckBillingIcon" {...ICON_PROPS} />
          <Typography
            color="pds-information-600"
            weight="semibold"
            leading="tight"
            className="co-whitespace-nowrap"
          >
            Recibe en
          </Typography>
          <Typography
            color="neutral-grey-500"
            className="co-whitespace-nowrap"
            weight={addressAlias ? 'semibold' : 'normal'}
            leading="tight"
            noWrap
          >
            {addressAlias ? addressAlias : address}
          </Typography>
        </div>

        {totalDeliveries < 2 && <Divider />}

        {addressAlias && (
          <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
            {isExpanded && (
              <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
                {addressAlias + ': '}
              </span>
            )}

            {address}
          </p>
        )}
        <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
          {addressDetails}
        </p>
      </div>
    </div>
  );
};

const HomeDeliveryDetail: FC<THomeDeliveryDetail> = ({
  homeDelivery,
  index,
  totalDeliveries
}) => {
  const dateLabel = formatDate(homeDelivery);

  const { address, addressDetails } = formatAddress('detail', homeDelivery);
  const addressAlias = homeDelivery?.addressAlias;

  return (
    <div
      className="co-w-full co-h-full co-flex co-items-center co-gap-8px lg:co-p-8px co-bg-white lg:co-bg-pds-neutral-100 co-rounded-4px"
      key={index}
      tabIndex={0}
    >
      <div className="co-w-full co-flex co-flex-col co-gap-8px">
        <div className="co-flex co-justify-between">
          {totalDeliveries > 1 && (
            <Typography weight="semibold" color="pds-neutral-700" size="body1">
              Entrega {index + 1}
            </Typography>
          )}

          <div className="co-flex co-items-center co-gap-4px co-overflow-hidden">
            <Icon icon="TruckBillingIcon" {...ICON_PROPS} />
            <Typography color="primary-500" weight="semibold" leading="tight">
              Recibe
            </Typography>
            <Typography
              color="neutral-grey-500"
              weight="semibold"
              leading="tight"
            >
              {dateLabel}
            </Typography>
          </div>
        </div>
        <Divider />
        <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
          {addressAlias && (
            <span className="co-text-body1 co-font-semibold co-text-neutral-grey-500">
              {addressAlias + ': '}
            </span>
          )}
          {address}
        </p>
        <p className="co-text-body1 co-text-neutral-grey-500 co-leading-tight">
          {addressDetails}
        </p>
      </div>
    </div>
  );
};

export { HomeDeliveryDetail, HomeDeliverySummary };
